/**
 * BusinessDashboard — Role-aware Business Command Dashboard
 * Default Home for business users (TENANT_ADMIN, EVENT_MANAGER, RELATIONSHIP_MANAGER, etc.).
 * Every metric is clickable and drills through to the authoritative source.
 *
 * Quick Actions visibility is driven strictly by effective capabilities
 * via hasCapability(user, cap) — never by hardcoded role strings.
 *
 * P-ARC-01R: SALES_CRM_USER and EVENT_POC removed; RELATIONSHIP_MANAGER and PROJECT_MANAGER added.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBrand } from "@/contexts/BrandContext";
import {
  AlertTriangle, Users, Clock, CheckSquare, CalendarDays,
  TrendingUp, Target, Activity, MapPin, RefreshCw, ChevronRight,
  BarChart3, Zap, ArrowRight, MessageCircle, AlertCircle,
  UserPlus, PhoneCall, Clock3, Building2, Search, UserCheck,
  FileText, Settings, Inbox, Map, X,
} from "lucide-react";
import { hasCapability } from "../services/api";

const API = process.env.REACT_APP_BACKEND_URL;

// Check if a user has ANY of the listed capabilities
const hasAnyCapability = (user, caps) =>
  Array.isArray(caps) && caps.some(c => hasCapability(user, c));

// ── Brand / risk colour tokens ────────────────────────────────────────────────
const RISK_COLOR = {
  BLOCKED: { bg: "bg-red-50", border: "border-red-200", badge: "bg-red-100 text-red-700", dot: "bg-red-500" },
  AT_RISK: { bg: "bg-amber-50", border: "border-amber-200", badge: "bg-amber-100 text-amber-700", dot: "bg-amber-500" },
  ON_TRACK: { bg: "bg-green-50", border: "border-green-200", badge: "bg-green-100 text-green-700", dot: "bg-green-500" },
};

const ATTENTION_COLORS = {
  red:    { card: "bg-red-50 border-red-200",    icon: "text-red-600",    badge: "bg-red-100 text-red-700" },
  orange: { card: "bg-amber-50 border-amber-200", icon: "text-amber-600",  badge: "bg-amber-100 text-amber-700" },
  amber:  { card: "bg-orange-50 border-orange-200",icon: "text-orange-600",badge: "bg-orange-100 text-orange-700" },
  blue:   { card: "bg-blue-50 border-blue-200",  icon: "text-blue-600",   badge: "bg-blue-100 text-blue-700" },
  green:  { card: "bg-green-50 border-green-200",icon: "text-green-600",  badge: "bg-green-100 text-green-700" },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function fmtDate(isoStr) {
  if (!isoStr) return "";
  try { return new Date(isoStr).toLocaleDateString("en-IN", { day: "numeric", month: "short" }); }
  catch { return isoStr; }
}

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Quick Actions Catalog — ordered by default priority ──────────────────────
// Visibility is driven by effective capabilities; role strings are never checked here.
// Up to 6 are displayed per user (highest priority that match their caps).
const QUICK_ACTIONS_CATALOG = [
  {
    id: "new-lead", priority: 1, label: "New Lead", desc: "Capture a new inquiry",
    capabilities: ["crm.lead.create"],
    Icon: UserPlus, accent: "#C5A059", action: "navigate", route: "/engage/leads",
  },
  {
    id: "my-leads", priority: 2, label: "My Leads", desc: "View your assigned leads",
    capabilities: ["crm.lead.read", "crm.lead.read_assigned"],
    Icon: Users, accent: "#0176D3", action: "navigate", route: "/engage/leads?assigned_to=me",
  },
  {
    id: "lead-queue", priority: 3, label: "Lead Queue", desc: "Review uncontacted leads",
    capabilities: ["crm.lead.read"],
    Icon: Inbox, accent: "#5E4DB2", action: "navigate", route: "/engage/leads?status=NEW",
  },
  {
    id: "log-activity", priority: 4, label: "Log Activity", desc: "Record a call or interaction",
    capabilities: ["crm.activity.create", "crm.activity.create_assigned"],
    Icon: PhoneCall, accent: "#2E844A", action: "pick-record", activityType: "CALL",
  },
  {
    id: "follow-up", priority: 5, label: "Follow-up", desc: "Schedule a reminder",
    capabilities: ["crm.followup.create", "crm.followup.create_assigned"],
    Icon: Clock3, accent: "#B45309", action: "navigate", route: "/engage/follow-ups",
  },
  {
    id: "add-meeting", priority: 6, label: "Add Meeting", desc: "Log a meeting with a lead",
    capabilities: ["crm.activity.create", "crm.activity.create_assigned"],
    Icon: CalendarDays, accent: "#0E7490", action: "pick-record", activityType: "MEETING",
  },
  {
    id: "add-note", priority: 7, label: "Add Note", desc: "Attach a note to a lead",
    capabilities: ["crm.activity.create", "crm.activity.create_assigned"],
    Icon: FileText, accent: "#64748B", action: "pick-record", activityType: "NOTE",
  },
  {
    id: "search-crm", priority: 8, label: "Search CRM", desc: "Find leads, accounts, contacts",
    capabilities: ["crm.lead.read", "crm.account.read", "crm.lead.read_assigned", "crm.account.read_assigned"],
    Icon: Search, accent: "#475569", action: "navigate", route: "/engage/leads",
  },
  {
    id: "opportunities", priority: 9, label: "Opportunities", desc: "View open opportunities",
    capabilities: ["crm.opportunity.read", "crm.opportunity.read_assigned"],
    Icon: Target, accent: "#15803D", action: "navigate", route: "/crm/opportunities",
  },
  {
    id: "assign-rm", priority: 10, label: "Assign RM", desc: "Assign Relationship Manager to a lead",
    capabilities: ["crm.lead.assign"],
    Icon: UserCheck, accent: "#C5A059", action: "navigate", route: "/engage/leads?status=NEW",
  },
  {
    id: "customer-journey", priority: 11, label: "Customer Journey", desc: "View active journeys",
    capabilities: ["journey.read", "journey.read_assigned"],
    Icon: Map, accent: "#7C3AED", action: "navigate", route: "/engage/leads",
  },
  {
    id: "crm-reports", priority: 12, label: "CRM Reports", desc: "Pipeline & conversion KPIs",
    capabilities: ["crm.report.exec"],
    Icon: TrendingUp, accent: "#8B5CF6", action: "navigate", route: "/home",
  },
  {
    id: "new-account", priority: 13, label: "New Account", desc: "Add a company or client",
    capabilities: ["crm.account.create"],
    Icon: Building2, accent: "#5E4DB2", action: "navigate", route: "/crm/accounts",
  },
  {
    id: "administration", priority: 14, label: "Administration", desc: "Manage platform settings",
    capabilities: ["admin.users"],
    Icon: Settings, accent: "#475569", action: "navigate", route: "/administration",
  },
];

// ── Quick Action Card ─────────────────────────────────────────────────────────
function QuickActionCard({ action, onClick }) {
  const [hovered, setHovered] = useState(false);
  const { Icon } = action;
  return (
    <button
      data-testid={`quick-action-${action.id}`}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex flex-col items-start gap-2.5 p-4 bg-white rounded-xl w-full text-left transition-all duration-200"
      style={{
        border: hovered ? `1px solid ${action.accent}` : "1px solid #E7E0D8",
        boxShadow: hovered
          ? `0 4px 16px -2px ${action.accent}28`
          : "0 2px 8px -1px rgba(28,25,23,0.04)",
        transform: hovered ? "translateY(-1px)" : "translateY(0)",
      }}
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${action.accent}18` }}
      >
        <Icon className="w-4 h-4" style={{ color: action.accent }} />
      </div>
      <div className="w-full">
        <p className="text-sm font-semibold leading-tight" style={{ color: "#1C1917" }}>
          {action.label}
        </p>
        <p className="text-[11px] mt-0.5 leading-snug" style={{ color: "#78716C" }}>
          {action.desc}
        </p>
      </div>
    </button>
  );
}

// ── Record Picker Modal (for activity-type actions that need a parent record) ─
function RecordPickerModal({ isOpen, onClose, onSelect, recentJourneys, actionLabel = "Log Activity" }) {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden"
        data-testid="record-picker-modal"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: "#E7E0D8" }}
        >
          <div>
            <h3 className="text-sm font-bold" style={{ color: "#1C1917" }}>
              Select a Lead
            </h3>
            <p className="text-[11px] mt-0.5" style={{ color: "#78716C" }}>
              Choose a lead to {actionLabel.toLowerCase()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg transition-colors"
            style={{ color: "#78716C" }}
            onMouseEnter={e => { e.currentTarget.style.background = "#F4EFEA"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
            data-testid="record-picker-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Lead list */}
        <div className="max-h-72 overflow-y-auto">
          {recentJourneys?.length > 0 ? (
            recentJourneys.slice(0, 6).map((j) => (
              <button
                key={j.lead_id || j.journey_id}
                data-testid={`record-picker-lead-${j.lead_id}`}
                onClick={() => onSelect(j)}
                className="w-full flex items-center gap-3 px-4 py-3 border-b text-left transition-colors"
                style={{ borderColor: "#F4EFEA" }}
                onMouseEnter={e => { e.currentTarget.style.background = "#FDFAF5"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0"
                  style={{ background: "var(--brand-gold)" }}
                >
                  {(j.customer_name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate" style={{ color: "#1C1917" }}>
                    {j.customer_name}
                  </p>
                  <p className="text-[11px] truncate" style={{ color: "#78716C" }}>
                    {j.lead_number} · {j.event_type || j.status}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: "#D6CEBF" }} />
              </button>
            ))
          ) : (
            <div className="px-4 py-8 text-center text-sm" style={{ color: "#78716C" }}>
              No recent leads found
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t" style={{ borderColor: "#E7E0D8", background: "#FDFAF5" }}>
          <button
            data-testid="record-picker-search-btn"
            onClick={() => onClose("navigate")}
            className="w-full text-xs font-medium py-1.5 transition-colors"
            style={{ color: "var(--brand-gold)" }}
            onMouseEnter={e => { e.currentTarget.style.color = "var(--brand-gold-hover)"; }}
            onMouseLeave={e => { e.currentTarget.style.color = "var(--brand-gold)"; }}
          >
            Search all leads →
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Quick Actions Panel ───────────────────────────────────────────────────────
function QuickActionsPanel({ user, onNavigate, recentJourneys }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerAction, setPickerAction] = useState(null);

  if (!user) return null;

  const visibleActions = QUICK_ACTIONS_CATALOG
    .filter(a => hasAnyCapability(user, a.capabilities))
    .slice(0, 6);

  if (!visibleActions.length) return null;

  const handleCardClick = (action) => {
    if (action.action === "pick-record") {
      setPickerAction(action);
      setPickerOpen(true);
    } else {
      onNavigate(action.route);
    }
  };

  const handlePickerClose = (mode) => {
    setPickerOpen(false);
    if (mode === "navigate") onNavigate("/engage/leads");
  };

  const handlePickerSelect = (journey) => {
    setPickerOpen(false);
    // Navigate to lead detail; user logs activity from there
    onNavigate(journey.route || `/engage/leads/${journey.lead_id}`);
  };

  const colClass =
    visibleActions.length <= 2 ? "grid-cols-2" :
    visibleActions.length === 3 ? "grid-cols-3" :
    visibleActions.length === 4 ? "grid-cols-2 sm:grid-cols-4" :
    visibleActions.length === 5 ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" :
    "grid-cols-2 sm:grid-cols-3 lg:grid-cols-6";

  return (
    <>
      <section data-testid="quick-actions-section">
        <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "#78716C" }}>
          Quick Actions
        </p>
        <div className={`grid ${colClass} gap-3`}>
          {visibleActions.map(action => (
            <QuickActionCard
              key={action.id}
              action={action}
              onClick={() => handleCardClick(action)}
            />
          ))}
        </div>
      </section>

      <RecordPickerModal
        isOpen={pickerOpen}
        onClose={handlePickerClose}
        onSelect={handlePickerSelect}
        recentJourneys={recentJourneys}
        actionLabel={pickerAction?.label}
      />
    </>
  );
}

// ── My Work Today — compact stat strip ────────────────────────────────────────
function WorkTodayStat({ chip, onClick }) {
  const [hovered, setHovered] = useState(false);
  const { Icon } = chip;
  const urgent = chip.count > 0;
  return (
    <button
      data-testid={`work-stat-${chip.id}`}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex items-center gap-2 px-3 py-1.5 rounded-full border transition-all duration-200 whitespace-nowrap"
      style={{
        background: urgent ? `${chip.urgentColor}14` : "white",
        borderColor: urgent ? (hovered ? chip.urgentColor : `${chip.urgentColor}55`) : (hovered ? "#C0B8AF" : "#E7E0D8"),
        color: urgent ? chip.urgentColor : "#78716C",
        transform: hovered ? "scale(1.02)" : "scale(1)",
      }}
    >
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      <span className="text-xs">
        <span className="font-bold">{chip.count}</span>{" "}{chip.label}
      </span>
    </button>
  );
}

function MyWorkTodayPanel({ myWork, onNavigate, user }) {
  if (!myWork || !user) return null;

  const chips = [];

  // Follow-ups due — user must have followup read access
  if (hasAnyCapability(user, ["crm.followup.read", "crm.followup.read_assigned"])) {
    chips.push({
      id: "follow-ups-due", label: "Follow-ups Due",
      count: myWork.follow_ups_due,
      Icon: Clock3, urgentColor: "#FE9339",
      link: "/engage/follow-ups?overdue=true",
    });
  }

  // My open leads — user must have lead read access
  if (hasAnyCapability(user, ["crm.lead.read", "crm.lead.read_assigned"])) {
    chips.push({
      id: "my-leads", label: "My Leads",
      count: myWork.my_open_leads,
      Icon: Users, urgentColor: "#0176D3",
      link: "/engage/leads?assigned_to=me",
    });
  }

  // New leads assigned today (only if > 0 and user can create/read leads)
  if (myWork.new_leads_today > 0 && hasAnyCapability(user, ["crm.lead.read", "crm.lead.create", "crm.lead.read_assigned"])) {
    chips.push({
      id: "new-today", label: "New Today",
      count: myWork.new_leads_today,
      Icon: Zap, urgentColor: "#2E844A",
      link: "/engage/leads?assigned_to=me",
    });
  }

  // Pending approvals assigned to this user (only if > 0)
  if (myWork.pending_approvals > 0) {
    chips.push({
      id: "pending-approvals", label: "Pending Approvals",
      count: myWork.pending_approvals,
      Icon: CheckSquare, urgentColor: "#5E4DB2",
      link: "/events?approvals=pending",
    });
  }

  if (!chips.length) return null;

  const allClear = chips.every(c => c.count === 0);

  return (
    <section data-testid="my-work-today-section">
      <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "#78716C" }}>
        My Work Today
      </p>
      <div className="flex flex-wrap gap-2 items-center">
        {allClear ? (
          <span
            data-testid="work-today-all-clear"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium"
            style={{ background: "#DCFCE7", color: "#15803D", border: "1px solid #BBF7D0" }}
          >
            <Activity className="w-3 h-3" />
            All clear — no pending work today
          </span>
        ) : (
          chips.map(chip => (
            <WorkTodayStat key={chip.id} chip={chip} onClick={() => onNavigate(chip.link)} />
          ))
        )}
      </div>
    </section>
  );
}

// ── Clickable metric card ────────────────────────────────────────────────────
function MetricCard({ value, label, link, color = "var(--brand-gold)", onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex flex-col items-start p-4 bg-white rounded-xl w-full text-left transition-all duration-200"
      style={{
        border: hovered ? "1px solid var(--brand-gold)" : "1px solid #E7E0D8",
        boxShadow: hovered ? "0 4px 16px -2px rgba(197,160,89,0.15)" : "0 2px 8px -1px rgba(28,25,23,0.04)",
      }}
    >
      <span className="text-2xl font-bold font-display" style={{ color: color === "#0176D3" ? "var(--brand-gold)" : color }}>{value}</span>
      <span className="text-xs mt-1 transition-colors" style={{ color: hovered ? "var(--brand-gold)" : "#78716C" }}>{label}</span>
    </button>
  );
}

// ── Needs Attention chip ─────────────────────────────────────────────────────
function AttentionItem({ item, onClick }) {
  const c = ATTENTION_COLORS[item.color] || ATTENTION_COLORS.blue;
  return (
    <button
      data-testid={`attention-${item.type}`}
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-left transition-all ${c.card}`}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = "0 4px 12px -2px rgba(28,25,23,0.08)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}
    >
      <AlertCircle className={`w-4 h-4 flex-shrink-0 ${c.icon}`} />
      <span className={`text-xs font-semibold ${c.badge.split(" ")[1]}`}>{item.label}</span>
      <ChevronRight className="w-3 h-3 ml-auto" style={{ color: "#D6CEBF" }} />
    </button>
  );
}

// ── Upcoming event row ───────────────────────────────────────────────────────
function EventRow({ event, onClick }) {
  const risk = RISK_COLOR[event.risk] || RISK_COLOR.ON_TRACK;
  return (
    <button
      data-testid={`upcoming-event-${event.event_id}`}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 transition-colors border-b last:border-b-0 text-left"
      style={{ borderColor: "#F4EFEA" }}
      onMouseEnter={e => { e.currentTarget.style.background = "#FDFAF5"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
    >
      <div className={`w-2 h-2 rounded-full flex-shrink-0 ${risk.dot}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate" style={{ color: "#1C1917" }}>{event.name}</p>
        <p className="text-xs flex items-center gap-1 mt-0.5" style={{ color: "#78716C" }}>
          <CalendarDays className="w-3 h-3" />
          {fmtDate(event.event_date)} · {event.event_type} · {event.guest_count} guests
        </p>
      </div>
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${risk.badge}`}>
        {event.risk === "ON_TRACK" ? "READY" : event.risk}
      </span>
    </button>
  );
}

// ── Customer Journey row ─────────────────────────────────────────────────────
function JourneyRow({ journey, onClick }) {
  const isWaiting = journey.agent_status === "WAITING_FOR_POC";
  return (
    <button
      data-testid={`journey-row-${journey.journey_id}`}
      onClick={onClick}
      className="w-full flex items-start gap-3 px-4 py-3 transition-colors border-b last:border-b-0 text-left"
      style={{ borderColor: "#F4EFEA" }}
      onMouseEnter={e => { e.currentTarget.style.background = "#FDFAF5"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
        style={{ background: "var(--brand-gold)" }}
      >
        {(journey.customer_name || "?")[0]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold truncate" style={{ color: "#1C1917" }}>{journey.customer_name}</p>
          {isWaiting && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 flex-shrink-0">
              WAITING ON US
            </span>
          )}
        </div>
        <p className="text-xs mt-0.5 truncate" style={{ color: "#78716C" }}>
          {journey.lead_number} · {journey.event_type || journey.stage || journey.status}
          {journey.event_date ? ` · ${fmtDate(journey.event_date)}` : ""}
        </p>
        {journey.last_interaction_at && (
          <p className="text-[10px] mt-0.5" style={{ color: "#78716C" }}>
            Last: {timeAgo(journey.last_interaction_at)}
          </p>
        )}
      </div>
      <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#D6CEBF" }} />
    </button>
  );
}

// ── Act Now row ──────────────────────────────────────────────────────────────
function ActNowRow({ action, rank, onClick }) {
  const sev = {
    CRITICAL: "text-red-600 bg-red-50",
    HIGH: "text-amber-600 bg-amber-50",
    MEDIUM: "text-blue-600 bg-blue-50",
  }[action.severity] || "text-[#78716C] bg-[#F4EFEA]";
  return (
    <button
      data-testid={`act-now-${rank}`}
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 transition-colors border-b last:border-b-0 text-left"
      style={{ borderColor: "#F4EFEA" }}
      onMouseEnter={e => { e.currentTarget.style.background = "#FDFAF5"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
    >
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[11px] font-bold"
        style={{ background: "var(--brand-gold)" }}
      >
        {rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate" style={{ color: "#1C1917" }}>{action.title}</p>
      </div>
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${sev}`}>
        {action.severity}
      </span>
      <ArrowRight className="w-4 h-4 flex-shrink-0" style={{ color: "#D6CEBF" }} />
    </button>
  );
}

// ── Section header ────────────────────────────────────────────────────────────
function SectionHeader({ title, count, link, onLinkClick }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "#E7E0D8" }}>
      <h3 className="text-xs font-bold uppercase tracking-widest" style={{ color: "#78716C" }}>{title}</h3>
      {count !== undefined && (
        <span className="text-xs font-medium" style={{ color: "#78716C" }}>{count}</span>
      )}
      {link && (
        <button
          onClick={onLinkClick}
          className="text-xs font-medium flex items-center gap-1 transition-colors"
          style={{ color: "var(--brand-gold)" }}
          onMouseEnter={e => { e.currentTarget.style.color = "var(--brand-gold-hover)"; }}
          onMouseLeave={e => { e.currentTarget.style.color = "var(--brand-gold)"; }}
        >
          View all <ChevronRight className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function BusinessDashboard() {
  const { user } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [crmData, setCrmData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const CRM_EXEC_ROLES = ["BUSINESS_OWNER", "CRM_MANAGER"];

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await fetch(`${API}/api/dashboard/business`, { credentials: "include" });
      if (res.ok) {
        const dashData = await res.json();
        setData(dashData);
        if (CRM_EXEC_ROLES.includes(dashData.role)) {
          try {
            const crmRes = await fetch(`${API}/api/crm/dashboard`, { credentials: "include" });
            if (crmRes.ok) setCrmData(await crmRes.json());
          } catch { /* crm analytics optional */ }
        }
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const nav = (link) => { if (link) navigate(link); };

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div
        className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full"
        style={{ borderColor: "var(--brand-gold)", borderTopColor: "transparent" }}
      />
    </div>
  );

  if (!data) return (
    <div className="flex items-center justify-center h-full" style={{ color: "#78716C" }}>
      Unable to load dashboard
    </div>
  );

  const role = data.role;
  const bt = data.business_today || data.my_day || data.crm_today || data.operations_today || {};
  const links = data.links || {};
  const isCrmExec = CRM_EXEC_ROLES.includes(role);

  // Build metrics from role-specific payload
  const metrics = [];
  if (role === "BUSINESS_OWNER" || role === "CRM_MANAGER") {
    const cp = data.crm_pipeline || {};
    metrics.push(
      { v: cp.total_leads,        l: "Total Leads",         lnk: links.total_leads },
      { v: cp.open_leads,         l: "Open Leads",          lnk: links.open_leads },
      { v: cp.qualified_leads,    l: "Qualified",           lnk: links.qualified_leads, color: "#2E844A" },
      { v: cp.converted_leads,    l: "Converted",           lnk: links.converted_leads, color: "#2E844A" },
      { v: cp.open_opportunities, l: "Opportunities",       lnk: links.open_opportunities },
      { v: cp.overdue_follow_ups, l: "Overdue Follow-ups",  lnk: "/engage/follow-ups", color: (cp.overdue_follow_ups || 0) > 0 ? "#FE9339" : undefined },
    );
  } else if (role === "TENANT_ADMIN") {
    metrics.push(
      { v: bt.open_leads,          l: "Open Leads",         lnk: links.open_leads },
      { v: bt.qualified_leads,     l: "Qualified Leads",    lnk: links.qualified_leads },
      { v: bt.open_opportunities,  l: "Opportunities",      lnk: links.open_opportunities },
      { v: bt.active_events,       l: "Active Events",      lnk: links.active_events },
      { v: bt.events_ready,        l: "Events Ready",       lnk: links.active_events, color: "#2E844A" },
      { v: bt.events_at_risk,      l: "Events At Risk",     lnk: links.events_at_risk, color: bt.events_at_risk > 0 ? "#FE9339" : undefined },
      { v: bt.events_blocked,      l: "Events Blocked",     lnk: links.events_blocked, color: bt.events_blocked > 0 ? "#BA0517" : undefined },
      { v: bt.pending_approvals,   l: "Pending Approvals",  lnk: links.pending_approvals, color: bt.pending_approvals > 0 ? "#FE9339" : undefined },
    );
  } else if (role === "EVENT_MANAGER") {
    metrics.push(
      { v: bt.my_leads,               l: "My Leads",          lnk: links.my_leads },
      { v: bt.follow_ups_due_today,   l: "Follow-ups Due",    lnk: links.follow_ups_due_today, color: bt.follow_ups_due_today > 0 ? "#FE9339" : undefined },
      { v: bt.customers_waiting,      l: "Customers Waiting", lnk: links.customers_waiting, color: bt.customers_waiting > 0 ? "#FE9339" : undefined },
      { v: bt.my_active_events,       l: "My Active Events",  lnk: links.my_active_events },
      { v: bt.events_at_risk,         l: "Events At Risk",    lnk: links.events_at_risk, color: bt.events_at_risk > 0 ? "#FE9339" : undefined },
      { v: bt.events_blocked,         l: "Events Blocked",    lnk: links.events_blocked, color: bt.events_blocked > 0 ? "#BA0517" : undefined },
      { v: bt.pending_approvals,      l: "Pending Approvals", lnk: links.pending_approvals, color: bt.pending_approvals > 0 ? "#FE9339" : undefined },
      { v: bt.critical_issues,        l: "Critical Issues",   lnk: links.critical_issues, color: bt.critical_issues > 0 ? "#BA0517" : undefined },
    );
  } else if (role === "RELATIONSHIP_MANAGER") {
    // P-ARC-01R: RELATIONSHIP_MANAGER (replaces SALES_CRM_USER for active data)
    metrics.push(
      { v: bt.new_leads,          l: "New Leads",         lnk: links.new_leads },
      { v: bt.unqualified_leads,  l: "Unqualified",       lnk: links.unqualified_leads },
      { v: bt.qualified_leads,    l: "Qualified",         lnk: links.qualified_leads, color: "#2E844A" },
      { v: bt.follow_ups_due,     l: "Follow-ups Due",    lnk: links.follow_ups_due, color: bt.follow_ups_due > 0 ? "#FE9339" : undefined },
      { v: bt.poc_review || bt.planning_ready, l: "Planning Ready", lnk: links.new_leads },
      { v: bt.open_opportunities, l: "Opportunities",     lnk: links.open_opportunities },
    );
  } else if (role === "SALES_CRM_USER") {
    // Legacy: SALES_CRM_USER is RETIRED but keep branch so old sessions don't break
    metrics.push(
      { v: bt.new_leads,         l: "New Leads",         lnk: links.new_leads },
      { v: bt.unqualified_leads, l: "Unqualified",       lnk: links.unqualified_leads },
      { v: bt.qualified_leads,   l: "Qualified",         lnk: links.qualified_leads, color: "#2E844A" },
      { v: bt.follow_ups_due,    l: "Follow-ups Due",    lnk: links.follow_ups_due, color: bt.follow_ups_due > 0 ? "#FE9339" : undefined },
      { v: bt.poc_review || bt.planning_ready, l: "Planning Ready", lnk: links.new_leads },
      { v: bt.open_opportunities, l: "Opportunities",    lnk: links.open_opportunities },
    );
  } else if (role === "PROJECT_MANAGER") {
    // P-ARC-01R: PROJECT_MANAGER — operational event execution
    metrics.push(
      { v: bt.active_events,      l: "Active Events",     lnk: links.active_events },
      { v: bt.events_at_risk,     l: "At Risk",           lnk: links.events_at_risk, color: bt.events_at_risk > 0 ? "#FE9339" : undefined },
      { v: bt.events_blocked,     l: "Blocked",           lnk: links.events_blocked, color: bt.events_blocked > 0 ? "#BA0517" : undefined },
      { v: bt.critical_issues,    l: "Critical Issues",   lnk: links.critical_issues, color: bt.critical_issues > 0 ? "#BA0517" : undefined },
      { v: bt.pending_approvals,  l: "Pending Approvals", lnk: links.pending_approvals },
      { v: bt.overdue_milestones, l: "Overdue Milestones",lnk: links.overdue_milestones, color: bt.overdue_milestones > 0 ? "#FE9339" : undefined },
    );
  } else if (role === "OPERATIONS_USER") {
    metrics.push(
      { v: bt.active_events,      l: "Active Events",     lnk: links.active_events },
      { v: bt.events_at_risk,     l: "At Risk",           lnk: links.events_at_risk, color: bt.events_at_risk > 0 ? "#FE9339" : undefined },
      { v: bt.events_blocked,     l: "Blocked",           lnk: links.events_blocked, color: bt.events_blocked > 0 ? "#BA0517" : undefined },
      { v: bt.critical_issues,    l: "Critical Issues",   lnk: links.critical_issues, color: bt.critical_issues > 0 ? "#BA0517" : undefined },
      { v: bt.overdue_milestones, l: "Overdue Milestones",lnk: links.overdue_milestones, color: bt.overdue_milestones > 0 ? "#FE9339" : undefined },
      { v: bt.pending_approvals,  l: "Pending Approvals", lnk: links.pending_approvals },
    );
  } else {
    // Generic / EVENT_PLANNER_DESIGNER fallback
    const btg = data.business_today || data.my_day || {};
    metrics.push(
      { v: btg.my_leads || btg.open_leads,               l: "My Leads",          lnk: "/engage/leads?assigned_to=me" },
      { v: btg.follow_ups_due_today,                     l: "Follow-ups Due",    lnk: "/engage/follow-ups", color: (btg.follow_ups_due_today || 0) > 0 ? "#FE9339" : undefined },
      { v: btg.active_events || btg.my_active_events,    l: "Active Events",     lnk: "/events" },
      { v: btg.pending_approvals,                        l: "Pending Approvals", lnk: "/events", color: (btg.pending_approvals || 0) > 0 ? "#FE9339" : undefined },
    );
  }

  return (
    <div className="h-full overflow-y-auto p-4 md:p-6 space-y-4" data-testid="business-dashboard">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div
        className="rounded-2xl p-5 sm:p-6 flex items-start justify-between flex-wrap gap-3"
        style={{
          background: "linear-gradient(135deg, #FDFAF5 0%, #F7EFE4 100%)",
          border: "1px solid #E7D8C0",
        }}
      >
        <div>
          <h1
            data-testid="dashboard-greeting"
            className="font-display text-2xl sm:text-3xl font-normal"
            style={{ color: "#1C1917" }}
          >
            {greeting()}{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </h1>
          <p className="text-sm mt-1" style={{ color: "#78716C" }}>
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </p>
          <p className="text-xs mt-1.5 uppercase tracking-widest" style={{ color: "var(--brand-gold)" }}>
            Prashasta Event Intelligence · {brand.productShortName}
          </p>
        </div>
        <button
          data-testid="dashboard-refresh"
          onClick={() => load(true)}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full transition-colors"
          style={{ color: "#78716C", border: "1px solid #E7D8C0", background: "white" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--brand-gold)"; e.currentTarget.style.color = "var(--brand-gold)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "#E7D8C0"; e.currentTarget.style.color = "#78716C"; }}
        >
          <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* ── Quick Actions ────────────────────────────────────────────── */}
      <QuickActionsPanel
        user={user}
        onNavigate={nav}
        recentJourneys={data.recent_journeys}
      />

      {/* ── My Work Today (user-scoped stat strip) ───────────────────── */}
      <MyWorkTodayPanel
        myWork={data.my_work_today}
        onNavigate={nav}
        user={user}
      />

      {/* ── Needs Attention ──────────────────────────────────────────── */}
      {data.needs_attention?.length > 0 && (
        <section data-testid="needs-attention-section">
          <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "#78716C" }}>
            Needs Your Attention
          </p>
          <div className="flex flex-wrap gap-2">
            {data.needs_attention.map((item) => (
              <AttentionItem key={item.type} item={item} onClick={() => nav(item.link)} />
            ))}
          </div>
        </section>
      )}

      {/* ── Business Today — metrics grid ────────────────────────────── */}
      {metrics.filter(m => m.v !== undefined).length > 0 && (
        <section data-testid="business-today-section">
          <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "#78716C" }}>
            {role === "BUSINESS_OWNER" || role === "CRM_MANAGER" ? "CRM Pipeline Overview" :
             role === "EVENT_MANAGER" ? "My Day" :
             role === "RELATIONSHIP_MANAGER" ? "CRM Today" :
             role === "SALES_CRM_USER" ? "CRM Today" :  /* legacy */
             role === "OPERATIONS_USER" ? "Operations Today" : "Your Business Today"}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {metrics.filter(m => m.v !== undefined).map(({ v, l, lnk, color }) => (
              <MetricCard key={l} value={v} label={l} color={color} onClick={() => nav(lnk)} />
            ))}
          </div>
        </section>
      )}

      {/* ── CRM Executive Analytics (BUSINESS_OWNER / CRM_MANAGER only) ──── */}
      {isCrmExec && crmData && (
        <CRMAnalyticsSection crmData={crmData} onNavigate={nav} role={role} />
      )}
      {isCrmExec && !crmData && !loading && (
        <div className="bg-white rounded-xl border p-6 text-center text-sm" style={{ borderColor: "#E7E0D8", color: "#78716C" }} data-testid="crm-analytics-loading">
          Loading CRM analytics…
        </div>
      )}

      {/* ── Main content — two columns (hide for CRM execs) ──────────── */}
      {!isCrmExec && (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* ── Upcoming Events ──────────────────────────────────────── */}
        <div className="bg-white rounded-xl" style={{ border: "1px solid #E7E0D8" }} data-testid="upcoming-events-section">
          <SectionHeader
            title="Upcoming Events"
            count={data.upcoming_events?.length}
            link={data.upcoming_events?.length > 0}
            onLinkClick={() => nav("/events")}
          />
          {data.upcoming_events?.length > 0 ? (
            data.upcoming_events.map((ev) => (
              <EventRow key={ev.event_id} event={ev} onClick={() => nav(ev.route)} />
            ))
          ) : (
            <div className="px-4 py-8 text-center text-sm" style={{ color: "#78716C" }}>No upcoming events</div>
          )}
        </div>

        {/* ── Act Now ──────────────────────────────────────────────── */}
        <div className="bg-white rounded-xl" style={{ border: "1px solid #E7E0D8" }} data-testid="act-now-section">
          <SectionHeader title="Act Now" count={data.act_now?.length} />
          {data.act_now?.length > 0 ? (
            data.act_now.map((action, i) => (
              <ActNowRow key={i} action={action} rank={i + 1} onClick={() => nav(action.link)} />
            ))
          ) : (
            <div className="px-4 py-8 text-center text-sm" style={{ color: "#78716C" }}>
              All clear — no immediate actions needed
            </div>
          )}
        </div>
      </div>
      )}

      {/* ── Recent Customer Journeys ─────────────────────────────────── */}
      <div className="bg-white rounded-xl" style={{ border: "1px solid #E7E0D8" }} data-testid="recent-journeys-section">
        <SectionHeader
          title="Recent Customer Journeys"
          count={data.recent_journeys?.length}
          link={data.recent_journeys?.length > 0}
          onLinkClick={() => nav("/engage/leads")}
        />
        {data.recent_journeys?.length > 0 ? (
          <div className="divide-y" style={{ borderColor: "#F4EFEA" }}>
            {data.recent_journeys.slice(0, 6).map((j) => (
              <JourneyRow
                key={j.journey_id}
                journey={j}
                onClick={() => navigate(`/journeys/${j.journey_id}`)}
              />
            ))}
          </div>
        ) : (
          <div className="px-4 py-8 text-center text-sm" style={{ color: "#78716C" }}>No active journeys</div>
        )}
      </div>
    </div>
  );
}


// ─── CRM Analytics Section (BUSINESS_OWNER / CRM_MANAGER) ────────────────────
function CRMAnalyticsSection({ crmData, onNavigate, role }) {
  const kpis = crmData.kpis || {};
  const stages = crmData.pipeline_stages || [];
  const trend  = crmData.monthly_trend || [];
  const topOpps = crmData.top_opportunities || [];
  const agents  = crmData.agent_performance || [];
  const isManager = role === "CRM_MANAGER";
  const maxStageCount = Math.max(...stages.map(s => s.count), 1);
  const maxTrend = Math.max(...trend.map(t => t.count), 1);

  function fmtINR(val) {
    if (!val && val !== 0) return "—";
    if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
    if (val >= 100000)   return `₹${(val / 100000).toFixed(1)}L`;
    if (val >= 1000)     return `₹${(val / 1000).toFixed(0)}K`;
    return `₹${val}`;
  }

  const STAGE_COLORS = {
    NEW:        "#706E6B",
    CONTACTED:  "#0176D3",
    QUALIFYING: "#5E4DB2",
    QUALIFIED:  "#2E844A",
    POC_REVIEW: "#8C4B00",
    CONVERTED:  "#2E844A",
  };

  return (
    <>
      {/* ── CRM Executive KPIs ────────────────────────────────────────── */}
      <section data-testid="crm-exec-kpis">
        <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "#78716C" }}>
          CRM Executive KPIs
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <CRMKPICard
            testId="crm-kpi-conversion"
            label="Conversion Rate"
            value={`${kpis.conversion_rate ?? 0}%`}
            sub={`${kpis.converted_leads ?? 0} of ${kpis.total_leads ?? 0} leads`}
            color="#15803D"
          />
          <CRMKPICard
            testId="crm-kpi-qualification"
            label="Qualification Rate"
            value={`${kpis.qualification_rate ?? 0}%`}
            sub={`${kpis.qualified_leads ?? 0} qualified leads`}
            color="var(--brand-gold)"
          />
          <CRMKPICard
            testId="crm-kpi-response"
            label="Avg Response Time"
            value={kpis.avg_response_hours != null
              ? kpis.avg_response_hours < 1
                ? `${Math.round(kpis.avg_response_hours * 60)}m`
                : `${kpis.avg_response_hours}h`
              : "—"}
            sub="lead → first interaction"
            color={kpis.avg_response_hours != null && kpis.avg_response_hours > 24 ? "#B45309" : "#15803D"}
          />
          <CRMKPICard
            testId="crm-kpi-opp-value"
            label="Pipeline Value"
            value={fmtINR(kpis.total_opp_pipeline_value)}
            sub={`${kpis.open_opportunities ?? 0} open opportunities`}
            color="#5E4DB2"
            onClick={() => onNavigate("/crm/opportunities")}
          />
        </div>
      </section>

      {/* ── Pipeline Funnel ───────────────────────────────────────────── */}
      <section data-testid="pipeline-funnel-section">
        <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "#78716C" }}>
          Lead Pipeline Funnel
        </p>
        <div className="bg-white border rounded-xl p-4 space-y-2" style={{ borderColor: "#E7E0D8" }}>
          {stages.map(({ stage, count, label }) => {
            const pct = Math.round((count / maxStageCount) * 100);
            const color = STAGE_COLORS[stage] || "#706E6B";
            return (
              <button
                key={stage}
                data-testid={`funnel-stage-${stage.toLowerCase()}`}
                onClick={() => onNavigate(`/engage/leads?status=${stage}`)}
                className="w-full flex items-center gap-3 group"
              >
                <span className="text-[10px] font-mono w-16 sm:w-24 text-right flex-shrink-0 text-[#706E6B] group-hover:text-[#181818] transition-colors">
                  {label}
                </span>
                <div className="flex-1 h-6 bg-[#F3F3F3] rounded overflow-hidden">
                  <div
                    className="h-full rounded transition-all duration-500 flex items-center justify-end pr-2"
                    style={{ width: `${Math.max(pct, 2)}%`, backgroundColor: color, opacity: 0.85 }}
                  >
                    {count > 0 && (
                      <span className="text-[10px] font-bold text-white">{count}</span>
                    )}
                  </div>
                </div>
                <span className="text-xs font-semibold text-[#444444] w-8 text-right flex-shrink-0">{count}</span>
              </button>
            );
          })}
          {/* Terminal row summary */}
          {crmData.terminal_counts && (
            <div className="pt-2 mt-1 border-t flex gap-4 text-[11px]" style={{ borderColor: "#E7E0D8", color: "#78716C" }}>
              {Object.entries(crmData.terminal_counts).map(([k, v]) => (
                <span key={k}><span className="font-mono" style={{ color: "#57534E" }}>{v}</span> {k.toLowerCase()}</span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Top Opportunities + Monthly Trend ────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Top Opportunities */}
        <div className="bg-white border rounded-xl" style={{ borderColor: "#E7E0D8" }} data-testid="top-opportunities-section">
          <SectionHeader
            title="Top Opportunities"
            link={topOpps.length > 0}
            onLinkClick={() => onNavigate("/crm/opportunities")}
          />
          {topOpps.length > 0 ? (
            <div className="divide-y" style={{ borderColor: "#F4EFEA" }}>
              {topOpps.map((opp, i) => (
                <div key={opp.id} data-testid={`top-opp-${i}`}
                  className="flex items-center gap-3 px-4 py-3">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 text-white text-[10px] font-bold"
                    style={{ background: "var(--brand-gold)" }}
                  >
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold truncate" style={{ color: "#1C1917" }}>{opp.name || "—"}</p>
                    <p className="text-[10px] truncate" style={{ color: "#78716C" }}>
                      {opp.account_name || "No account"} · <span className="font-mono">{opp.stage || "—"}</span>
                    </p>
                  </div>
                  <span className="text-xs font-bold flex-shrink-0" style={{ color: "#15803D" }}>
                    {fmtINR(opp.estimated_value)}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="px-4 py-8 text-center text-sm" style={{ color: "#78716C" }}>
              No open opportunities
            </div>
          )}
        </div>

        {/* Monthly Trend */}
        <div className="bg-white border rounded-xl" style={{ borderColor: "#E7E0D8" }} data-testid="monthly-trend-section">
          <SectionHeader title="Lead Intake — Last 6 Months" />
          <div className="px-4 py-4">
            {trend.length > 0 ? (
              <div className="flex items-end gap-2 h-28">
                {trend.map(({ month, count }) => {
                  const barH = Math.max((count / maxTrend) * 100, 4);
                  return (
                    <div key={month} className="flex-1 flex flex-col items-center gap-1">
                      <span className="text-[9px] font-mono" style={{ color: "#78716C" }}>{count}</span>
                      <div
                        className="w-full rounded-t-sm"
                        style={{ height: `${barH}%`, background: "var(--brand-gold)", opacity: 0.8 }}
                        title={`${month}: ${count} leads`}
                      />
                      <span className="text-[8px] truncate w-full text-center" style={{ color: "#78716C" }}>{month}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center text-sm py-8" style={{ color: "#78716C" }}>No data</div>
            )}
            <p className="text-[10px] mt-3 text-center" style={{ color: "#78716C" }}>
              Total: <span className="font-semibold" style={{ color: "#1C1917" }}>{kpis.total_leads ?? 0}</span> leads all time
            </p>
          </div>
        </div>
      </div>

      {/* ── Per-Agent Performance (CRM_MANAGER only) ─────────────────── */}
      {isManager && (
        <section data-testid="agent-performance-section">
          <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: "#78716C" }}>
            Team Performance
          </p>
          <div className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: "#E7E0D8" }}>
            {agents.length > 0 ? (
              <table className="w-full text-xs" data-testid="agent-performance-table">
                <thead>
                  <tr className="border-b" style={{ borderColor: "#E7E0D8", background: "#FAF7F2" }}>
                    <th className="text-left px-4 py-2.5 font-semibold" style={{ color: "#57534E" }}>Agent</th>
                    <th className="text-right px-3 py-2.5 font-semibold" style={{ color: "#57534E" }}>Total</th>
                    <th className="text-right px-3 py-2.5 font-semibold" style={{ color: "#57534E" }}>Open</th>
                    <th className="text-right px-3 py-2.5 font-semibold" style={{ color: "#57534E" }}>Qualified</th>
                    <th className="text-right px-3 py-2.5 font-semibold" style={{ color: "#57534E" }}>Converted</th>
                    <th className="text-right px-4 py-2.5 font-semibold" style={{ color: "#57534E" }}>Conv %</th>
                  </tr>
                </thead>
                <tbody className="divide-y" style={{ borderColor: "#F4EFEA" }}>
                  {agents.map((ag, i) => (
                    <tr key={ag.agent_id || `unassigned-${i}`}
                      data-testid={`agent-row-${i}`}
                      className="transition-colors"
                      onMouseEnter={e => { e.currentTarget.style.background = "#FDFAF5"; }}
                      onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}
                    >
                      <td className="px-4 py-2.5 font-medium" style={{ color: "#1C1917" }}>
                        <div className="flex items-center gap-2">
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                            style={{ background: "var(--brand-gold-light)", border: "1px solid rgba(197,160,89,0.3)" }}
                          >
                            <span className="text-[9px] font-bold" style={{ color: "var(--brand-gold)" }}>
                              {ag.agent_name?.[0]?.toUpperCase() || "?"}
                            </span>
                          </div>
                          <span style={{ color: ag.agent_id ? "#1C1917" : "#78716C", fontStyle: ag.agent_id ? "normal" : "italic" }}>
                            {ag.agent_name}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono" style={{ color: "#57534E" }}>
                        <button
                          onClick={() => ag.agent_id ? onNavigate(`/engage/leads?poc=${ag.agent_id}`) : onNavigate("/engage/leads")}
                          className="hover:underline transition-colors"
                          style={{ color: "#57534E" }}
                          onMouseEnter={e => { e.currentTarget.style.color = "var(--brand-gold)"; }}
                          onMouseLeave={e => { e.currentTarget.style.color = "#57534E"; }}
                        >{ag.total}</button>
                      </td>
                      <td className="px-3 py-2.5 text-right font-mono" style={{ color: "#57534E" }}>{ag.open}</td>
                      <td className="px-3 py-2.5 text-right font-mono" style={{ color: "#15803D" }}>{ag.qualified}</td>
                      <td className="px-3 py-2.5 text-right font-mono font-semibold" style={{ color: "#15803D" }}>{ag.converted}</td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="font-semibold" style={{ color: ag.conv_rate >= 20 ? "#15803D" : ag.conv_rate >= 10 ? "#B45309" : "#78716C" }}>
                          {ag.conv_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-4 py-8 text-center text-sm" style={{ color: "#78716C" }}>
                No agent assignments found
              </div>
            )}
          </div>
        </section>
      )}
    </>
  );
}

function CRMKPICard({ testId, label, value, sub, color, onClick }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="flex flex-col items-start p-4 bg-white rounded-xl w-full text-left transition-all duration-200"
      style={{
        border: hovered ? "1px solid var(--brand-gold)" : "1px solid #E7E0D8",
        boxShadow: hovered ? "0 4px 16px -2px rgba(197,160,89,0.15)" : "none",
      }}
    >
      <span className="text-2xl font-bold font-display" style={{ color }}>{value}</span>
      <span className="text-xs font-semibold mt-1" style={{ color: "#1C1917" }}>{label}</span>
      {sub && <span className="text-[10px] mt-0.5" style={{ color: "#78716C" }}>{sub}</span>}
    </button>
  );
}
